import React, { useState } from 'react';
import {
  Card,
  CardContent,
  Grid,
  IconButton,
  Menu,
  MenuItem,
  Pagination,
  Alert,
  Tooltip,
  Chip,
  FormControl,
  InputLabel,
  Select,
} from '@mui/material';
import Icon from '@mui/material/Icon';
import { useNavigate } from '@tanstack/react-router';
import MDBox from 'src/components/md/MDBox';
import MDTypography from 'src/components/md/MDTypography';
import FileIcon from './FileIcon';
import { DocumentStatus, UserDocument } from 'src/types/UserDocuments';

interface DocumentListProps {
  documents: UserDocument[];
  isLoading: boolean;
  error?: Error | null;
  onDownload: (document: UserDocument) => void;
  onDelete: (documentId: string) => void;
  onRefresh: () => void;
  total: number;
  currentPage: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
  viewMode: ViewMode;
}

type ViewMode = 'grid' | 'list';

const formatFileSize = (bytes?: number): string => {
  if (!bytes || bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

const formatDate = (dateString?: string): string => {
  if (!dateString) return 'Unknown';
  try {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return 'Invalid date';
  }
};

const DocumentList: React.FC<DocumentListProps> = ({
  documents,
  isLoading,
  error,
  onDownload,
  onDelete,
  total,
  currentPage,
  pageSize,
  onPageChange,
  onPageSizeChange,
  viewMode,
}) => {
  const navigate = useNavigate();
  const [menuAnchor, setMenuAnchor] = useState<{
    el: HTMLElement;
    document: UserDocument;
  } | null>(null);

  const handleDocumentClick = (document: UserDocument) => {
    navigate({
      to: '/documents/$documentId',
      params: { documentId: document.id },
    });
  };

  const handleMenuOpen = (
    event: React.MouseEvent<HTMLElement>,
    document: UserDocument,
  ) => {
    setMenuAnchor({ el: event.currentTarget, document });
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
  };

  const handleDownload = () => {
    if (menuAnchor) {
      onDownload(menuAnchor.document);
      handleMenuClose();
    }
  };

  const handleDelete = () => {
    if (
      menuAnchor &&
      window.confirm('Are you sure you want to delete this document?')
    ) {
      onDelete(menuAnchor.document.id);
      handleMenuClose();
    }
  };

  const getStatusColor = (
    status: DocumentStatus,
  ): 'success' | 'warning' | 'default' => {
    switch (status) {
      case DocumentStatus.uploaded:
        return 'success';
      case DocumentStatus.pending:
        return 'warning';
      default:
        return 'default';
    }
  };

  const renderGridView = () => (
    <Grid container spacing={3}>
      {documents.map((document) => (
        <Grid item xs={12} sm={6} md={4} lg={3} key={document.id}>
          <Card
            sx={{
              cursor: 'pointer',
              transition: 'all 0.2s ease-in-out',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: 4,
              },
            }}
            onClick={() => handleDocumentClick(document)}
          >
            <CardContent sx={{ textAlign: 'center', p: 3 }}>
              <MDBox mb={2} display="flex" justifyContent="center">
                <FileIcon
                  fileName={document.name}
                  contentType={document.mimeType}
                  size="large"
                />
              </MDBox>

              <Tooltip title={document.name}>
                <MDTypography
                  variant="h6"
                  fontWeight="medium"
                  sx={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    mb: 1,
                  }}
                >
                  {document.name}
                </MDTypography>
              </Tooltip>

              <MDBox mb={1}>
                <Chip
                  label={document.status}
                  size="small"
                  color={getStatusColor(document.status)}
                  variant="outlined"
                />
              </MDBox>

              <MDTypography
                variant="caption"
                color="text"
                display="block"
                mb={1}
              >
                {formatFileSize(document.fileSizeBytes)}
              </MDTypography>

              <MDTypography
                variant="caption"
                color="text"
                display="block"
                mb={2}
              >
                {formatDate(document.createdAt)}
              </MDTypography>

              <MDBox display="flex" justifyContent="center" gap={1}>
                <Tooltip title="Download">
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      onDownload(document);
                    }}
                    disabled={document.status !== 'uploaded'}
                    color="info"
                  >
                    <Icon>download</Icon>
                  </IconButton>
                </Tooltip>

                <Tooltip title="More actions">
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleMenuOpen(e, document);
                    }}
                  >
                    <Icon>more_vert</Icon>
                  </IconButton>
                </Tooltip>
              </MDBox>
            </CardContent>
          </Card>
        </Grid>
      ))}
    </Grid>
  );

  const renderListView = () => (
    <Card>
      <CardContent sx={{ p: 0 }}>
        {documents.map((document, index) => (
          <MDBox
            key={document.id}
            p={2}
            display="flex"
            alignItems="center"
            sx={{
              borderBottom:
                index < documents.length - 1 ? '1px solid #e0e0e0' : 'none',
              cursor: 'pointer',
              '&:hover': {
                backgroundColor: '#f8f9fa',
              },
            }}
            onClick={() => handleDocumentClick(document)}
          >
            <FileIcon
              fileName={document.name}
              contentType={document.mimeType}
              size="medium"
            />

            <MDBox ml={2} flexGrow={1}>
              <MDTypography variant="body1" fontWeight="medium" mb={0.5}>
                {document.name}
              </MDTypography>
              <MDBox display="flex" alignItems="center" gap={1}>
                <Chip
                  label={document.status}
                  size="small"
                  color={getStatusColor(document.status)}
                  variant="outlined"
                />
                <MDTypography variant="caption" color="text">
                  {formatFileSize(document.fileSizeBytes)}
                </MDTypography>
                <MDTypography variant="caption" color="text">
                  {formatDate(document.createdAt)}
                </MDTypography>
              </MDBox>
            </MDBox>

            <MDBox display="flex" alignItems="center" gap={1}>
              <Tooltip title="Download">
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    onDownload(document);
                  }}
                  disabled={document.status !== 'uploaded'}
                  color="info"
                >
                  <Icon>download</Icon>
                </IconButton>
              </Tooltip>

              <Tooltip title="More actions">
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleMenuOpen(e, document);
                  }}
                >
                  <Icon>more_vert</Icon>
                </IconButton>
              </Tooltip>
            </MDBox>
          </MDBox>
        ))}
      </CardContent>
    </Card>
  );

  if (error) {
    return <Alert severity="error">{error.message}</Alert>;
  }

  return (
    <MDBox>
      {/* Document Count */}
      <MDBox mb={2}>
        <MDTypography variant="body2" color="text">
          Showing {documents.length} of {total} documents
        </MDTypography>
      </MDBox>

      {/* Documents */}
      {isLoading ? (
        <MDBox textAlign="center" p={6}>
          <MDTypography variant="body1" color="text">
            Loading documents...
          </MDTypography>
        </MDBox>
      ) : documents.length === 0 ? (
        <MDBox textAlign="center" p={6}>
          <Icon sx={{ fontSize: 64, color: '#bdbdbd', mb: 2 }}>
            folder_open
          </Icon>
          <MDTypography variant="h6" color="text" mb={1}>
            No documents found
          </MDTypography>
          <MDTypography variant="body2" color="text">
            Upload your first document to get started
          </MDTypography>
        </MDBox>
      ) : viewMode === 'grid' ? (
        renderGridView()
      ) : (
        renderListView()
      )}

      {/* Pagination */}
      {total > pageSize && (
        <MDBox
          mt={4}
          display="flex"
          justifyContent="center"
          alignItems="center"
          gap={2}
        >
          <Pagination
            count={Math.ceil(total / pageSize)}
            page={currentPage}
            onChange={(_, page) => onPageChange(page)}
            color="primary"
            showFirstButton
            showLastButton
          />
          <FormControl size="small" sx={{ minWidth: 100 }}>
            <InputLabel>Per page</InputLabel>
            <Select
              value={pageSize}
              label="Per page"
              onChange={(e) => onPageSizeChange(Number(e.target.value))}
            >
              <MenuItem value={10}>10</MenuItem>
              <MenuItem value={25}>25</MenuItem>
              <MenuItem value={50}>50</MenuItem>
              <MenuItem value={100}>100</MenuItem>
            </Select>
          </FormControl>
        </MDBox>
      )}

      {/* Context Menu */}
      <Menu
        anchorEl={menuAnchor?.el}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
      >
        <MenuItem
          onClick={handleDownload}
          disabled={menuAnchor?.document.status !== 'uploaded'}
        >
          <Icon sx={{ mr: 1 }}>download</Icon>
          Download
        </MenuItem>
        <MenuItem onClick={handleDelete}>
          <Icon sx={{ mr: 1 }}>delete</Icon>
          Delete
        </MenuItem>
      </Menu>
    </MDBox>
  );
};

export default DocumentList;
